import router from '@/router'
import { useUserStore } from '@/modules/user/stores/user.store'
import { useClientStore } from '@/modules/client/stores/client.store'
import { HttpMethod, HttpStatusCode } from '../types/http.enums'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import type { HttpRequest } from '../types/http.request'
import type { HttpResponse } from '../types/http.response'
import type { ErrorResponse } from '../types/error-response'

const API_VERSION_PATH = import.meta.env.VITE_API_VERSION_PATH

const HEADER_CONTENT_TYPE = 'Content-Type'
const HEADER_CONTENT_TYPE_VALUE = 'application/json'
const HEADER_ADDIMOTION_CLIENT = 'Addimotion-Client'

const MODE_CORS = 'cors'
const CREDENTIALS = 'include'

const buildHeaders = (request: RequestInit): HeadersInit => {
  const headers = new Headers(request.headers)
  headers.set(HEADER_CONTENT_TYPE, HEADER_CONTENT_TYPE_VALUE)

  const userStore = useUserStore()
  if (userStore.activeClient) {
    headers.set(HEADER_ADDIMOTION_CLIENT, userStore.activeClient.clientKey)
  }

  return headers
}

const buildUrl = (baseUrl: string, resource: string, useView: boolean = false): URL => {
  const clientStore = useClientStore()
  const endpoint = useView ? `views/${clientStore.activeViewId}/${resource}` : resource
  return new URL(`${API_VERSION_PATH}/${endpoint}`, baseUrl)
}

const parseJson = async <T>(response: Response): Promise<T | undefined> => {
  const contentType = response.headers.get(HEADER_CONTENT_TYPE)
  if (!contentType?.includes(HEADER_CONTENT_TYPE_VALUE)) {
    return undefined
  }

  return (await response.json()) as T
}

const handleHttpRedirects = async (status: number) => {
  switch (status) {
    case HttpStatusCode.Unauthorized:
      await router.push({ name: ROUTE_NAMES.UNAUTHORIZED })
      break
    case HttpStatusCode.Forbidden:
      await router.push({ name: ROUTE_NAMES.FORBIDDEN })
      break
    case HttpStatusCode.ServiceUnavailable:
      await router.push({ name: ROUTE_NAMES.SERVICE_UNAVAILABLE })
      break
  }
}

export const tryFetch = async <TResponse, TErrorCode, TBody = unknown>(
  method: HttpMethod,
  baseUrl: string,
  resource: string,
  options?: HttpRequest<TBody>,
): Promise<HttpResponse<TResponse, TErrorCode>> => {
  const request: RequestInit = {
    method,
    mode: MODE_CORS,
    credentials: CREDENTIALS,
  }
  try {
    request.headers = buildHeaders(request)

    if (options?.body) {
      request.body = JSON.stringify(options.body)
    }

    const response = await fetch(buildUrl(baseUrl, resource, options?.view), request)

    const status = response.status
    if (status === HttpStatusCode.NoContent) {
      return { status }
    }

    const json = await parseJson<unknown>(response)

    if (!response.ok) {
      await handleHttpRedirects(status)
      return {
        status,
        error: json as ErrorResponse<TErrorCode>,
      }
    }

    return {
      status,
      data: json as TResponse,
    }
  } catch (error: unknown) {
    return {
      status: 0,
      error: {
        errorCode: undefined as TErrorCode,
        context: {
          message: error instanceof Error ? error.message : 'Network error',
        },
      },
    }
  }
}
